import { useSyncExternalStore } from 'react';
import config from 'config';
import getCurrentUrl from 'utils/current-url';

const NAVIGATION_TRACKING_GLOBAL_KEY = '__WTR_NAVIGATION_TRACKING_PATCHED__' as const;

const subscribers = new Set<() => void>();

let previousPathname: string;

function getClientPathname(): string {
  return window.location.pathname;
}

function getServerPathname(): string {
  let currentUrl;

  try {
    currentUrl = getCurrentUrl();
  } catch {
    // Swallow
  }

  const parsed = new URL(currentUrl ?? '', config.clientHost);

  return parsed.pathname;
}

function notifySubscribersOnChange() {
  const currentPathname = getClientPathname();

  if (previousPathname === currentPathname) {
    return;
  }

  previousPathname = currentPathname;

  subscribers.forEach(callback => callback());
}

function initialisePathnameTracking() {
  if (window[NAVIGATION_TRACKING_GLOBAL_KEY]) {
    return;
  }

  window[NAVIGATION_TRACKING_GLOBAL_KEY] = true;
  previousPathname = getClientPathname();

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function pushState(...args) {
    originalPushState.apply(this, args);

    notifySubscribersOnChange();
  };

  window.history.replaceState = function replaceState(...args) {
    originalReplaceState.apply(this, args);

    notifySubscribersOnChange();
  };

  window.addEventListener('popstate', notifySubscribersOnChange);
}

function subscribe(callback: () => void): () => void {
  subscribers.add(callback);

  return () => {
    subscribers.delete(callback);
  };
}

export function usePathname(): string {
  initialisePathnameTracking();

  return useSyncExternalStore(subscribe, getClientPathname, getServerPathname);
}
